import { useEffect, useState } from "react";

/**
 * One scan's images at the size of the window.
 *
 * A review screen's thumbnails are small enough to place the card, not to read
 * a slab label or judge a corner — and that reading is exactly what confirming
 * asks the reviewer to do. So the thumbnails stay small and this is the way to
 * look properly, rather than growing the page around an image most reviewers
 * only glance at.
 */
export function ImageLightbox({
  urls,
  startIndex = 0,
  onClose,
}: {
  urls: string[];
  startIndex?: number;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(startIndex);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + urls.length) % urls.length);
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % urls.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, urls.length]);

  if (urls.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/90" />
      <img
        src={urls[idx]}
        alt={`Card ${idx + 1}`}
        className="relative max-h-full max-w-full rounded-row object-contain"
      />
      <button type="button" onClick={onClose} className="btn-chip absolute right-4 top-4">
        Close
      </button>
      {urls.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous image"
            onClick={() => setIdx((i) => (i - 1 + urls.length) % urls.length)}
            className="absolute left-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-2xl leading-none text-white"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Next image"
            onClick={() => setIdx((i) => (i + 1) % urls.length)}
            className="absolute right-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-2xl leading-none text-white"
          >
            ›
          </button>
        </>
      )}
    </div>
  );
}
